import { Col, ConfigProvider, DatePicker, Form, Input, Row, Select, Table } from "antd";
import React, { Fragment, useEffect, useState } from "react";
import dayjs from "dayjs";
import { ColumnsType } from "antd/es/table";
import { Link } from "react-router-dom";
import "./BudgetTrackingScreen.css";
import { TextHeaderComponents } from "../../components/TextHeaderComponents/TextHeaderComponents";
import { exportExcel } from "../../Helper/ExportExcel";
import {
  budgetFilter,
  GetPermissionReport,
  GetReportBudget,
  GetReportBudgetFilter,
  GetReportBudgetSummaries,
} from "../../Services/BudgetReportService";
import moment from "moment";
import { Button } from "primereact/button";

type DataNode = {
  key: string;
  type: string;
  desc: string;
  summary_alloc: number;
  summary_reserve: number;
  summary_used: number;
  summary_paid: number;
  summary_remaining: number;
  memoId?: number;
  children: DataNode[];
};

type BudgetReportItem = {
  key: string;
  type: string;
  desc: string;
  summary_alloc: number;
  summary_reserve: number;
  summary_used: number;
  summary_paid: number;
  summary_remaining: number;
  summary_cancelled: number;
  memoid?: number;
  children: BudgetReportItem[];
};

type FilterType = {
  label: string;
  value: any;
};

const Type = {
  BUDGET: "BUDGET",
  PR: "PR",
  PO: "PO",
  PV: "PV",
  GR: "GR",
}

function renderDescription(value: any, record: any) {
  let val = record.memoId ? (
    <Link
      className="text-form-running"
      target="_blank"
      to={`/Request?MemoID=${record.memoId}`}
    >
      {value}
    </Link>
  ) : (
    value
  );
  switch (record.type) {
    case Type.BUDGET:
      return <b style={{ color: "#282f6a" }}>{val}</b>;
    case Type.PR:
      return <b>{val}</b>;
    case Type.PV:
      return <span style={{ color: "grey" }}>{val}</span>;
    case Type.GR:
      return <i style={{ color: "grey" }}>{val}</i>;
    default:
      return val;
  }
}

function renderNumber(value: number, record: any, index: number, options: any) {
  let val = value ? value.toLocaleString() : "-";
  let view = null;
  let color = "#282f6a";
  if (options.col == "remaining") {
    let prefix = options.prefix;
    let alloc = record[`${prefix}_alloc`];
    let remain = record[`${prefix}_remaining`];
    if (alloc && remain) {
      let percentage = remain / alloc;
      if (percentage < 0.1) {
        color = "#f00";
      } else if (percentage < 0.2) {
        color = "#f80";
      }
    }
  }
  switch (record.type) {
    case Type.BUDGET:
      view = <b style={{ color: color }}>{val}</b>;
      break;
    case Type.PR:
      view = <b>{val}</b>;
      break;
    case Type.PV:
      view = <i style={{ color: "grey" }}>{val}</i>;
      break;
    default:
      view = val;
  }
  return <div style={{ textAlign: "right", width: "100%" }}>{view}</div>;
}

function genBudgetColumn(prefix = "", options = {}) {
  const budgetColumn: ColumnsType<any> = [
    {
      title: "ALLOCATE",
      dataIndex: `${prefix}_alloc`,
      key: `${prefix}_alloc`,
      ...options,
      render: (value, record, index) =>
        renderNumber(value, record, index, { prefix: prefix, col: "alloc" }),
    },
    {
      title: "RESERVED",
      dataIndex: `${prefix}_reserve`,
      key: `${prefix}_reserve`,
      ...options,
      render: (value, record, index) =>
        renderNumber(value, record, index, { prefix: prefix, col: "col" }),
    },
    {
      title: "USED",
      dataIndex: `${prefix}_used`,
      key: `${prefix}_used`,
      ...options,
      render: (value, record, index) =>
        renderNumber(value, record, index, { prefix: prefix, col: "used" }),
    },
    {
      title: "PAID",
      dataIndex: `${prefix}_paid`,
      key: `${prefix}_paid`,
      ...options,
      render: (value, record, index) =>
        renderNumber(value, record, index, { prefix: prefix, col: "paid" }),
    },
    {
      title: "REMAINING",
      dataIndex: `${prefix}_remaining`,
      key: `${prefix}_remaining`,
      ...options,
      render: (value, record, index) =>
        renderNumber(value, record, index, {
          prefix: prefix,
          col: "remaining",
        }),
    },
  ];
  return budgetColumn;
}

function BudgetSummary() {
  const columns: ColumnsType<any> = [
    {
      title: "Description",
      dataIndex: "desc",
      key: "desc",
      width: 300,
      render: (value, record, index) => renderDescription(value, record),
    },
    {
      title: "Summary",
      key: "key",
      children: genBudgetColumn("summary"),
    },
  ];

  const [data, setData] = useState<BudgetReportItem[]>([]);
  const [filteredData, setFilteredData] = useState<BudgetReportItem[]>([]);
  const [filters, setFilters] = useState<{
    cost: FilterType[];
    gl: FilterType[];
    io: FilterType[];
    year: FilterType[];
  }>({
    cost: [],
    gl: [],
    io: [],
    year: [],
  });
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [form] = Form.useForm();
  const [showExportSum, setShowExportSum] = useState(false);
  
  useEffect(() => {
    getReport();
    getReportFilter();
  }, []);

  const getReport = async (
    filterVal: budgetFilter = {
      budgetYear: moment().get("year"),
      description: "",
      costCenter: "",
      glAccount: "",
      internalOrder: "",
    },
    pageSize: number = 1
  ) => {
    setLoading(true);
    const data: { totalCount: number; reportData: BudgetReportItem[]} =
      await GetReportBudget({
        pageNumber: pageSize,
        pageSize: 10,
        filter: filterVal,
      });
      console.log(data);
      if(data){
      setTotal(data.totalCount);
      setData(data.reportData);
      setCurrentPage(pageSize);
      }

    setLoading(false);
  };

  const getReportFilter = async () => {
    setLoading(true);
    const resFilter = await GetReportBudgetFilter();
    setFilters(resFilter);
    setLoading(false);
  };


  const filterFunc = async (changedValues: any, allValues: any) => {
    setLoading(true);
    if (changedValues["budgetYear"]) {
      const resPermission = await GetPermissionReport({
        pageNumber: 1,
        pageSize: 100000,
        filter: {
          budgetYear: changedValues["budgetYear"],
          description: "",
          costCenter: "",
          glAccount: "",
          internalOrder: "",
        },
      });
      setShowExportSum(resPermission);
      await getReport({
        budgetYear: changedValues["budgetYear"],
        description: "",
        costCenter: "",
        glAccount: "",
        internalOrder: "",
      });
    } else {
      const result = filterReport(
        allValues.description
          ? data
              ?.map((e) => filterItemWithDescription(e, allValues.description))
              .filter((e): e is BudgetReportItem => e !== null)
          : data,
        allValues.costCenter,
        allValues.gl,
        allValues.io
      );
      setFilteredData(result);
    }
    setLoading(false);
  };


  function filterItemWithDescription(
    item: BudgetReportItem,
    descKeyword: string
  ): BudgetReportItem | null {
    const hasMatchingDescription =
      descKeyword && item.desc && item.desc.includes(descKeyword);

    const filteredChildren = item.children
      ?.map((child) => filterItemWithDescription(child, descKeyword))
      .filter((child): child is BudgetReportItem => child !== null);

    if (hasMatchingDescription || filteredChildren?.length > 0) {
      return {
        key: item.key,
        desc: item.desc,
        memoid: item.memoid,
        summary_alloc: item.summary_alloc,
        summary_remaining: item.summary_remaining,
        summary_paid: item.summary_paid,
        summary_reserve: item.summary_reserve,
        summary_used: item.summary_used,
        summary_cancelled: item.summary_cancelled,
        type: item.type,
        children: hasMatchingDescription ? item.children : filteredChildren,
      };
    }

    return null;
  }
  function filterReport(
    data: BudgetReportItem[],
    costKey: string,
    glKey: string,
    ioKey: string
  ): BudgetReportItem[] {
    return data
      ?.filter((item) => !costKey || (item.desc && item.desc.includes(costKey)))
      .map((item) => {
        item.children = item.children
          ?.filter((gl) => !glKey || (gl.desc && gl.desc.includes(glKey)))
          .map((gl) => {
            gl.children = gl.children
              .filter((io) => !ioKey || (io.desc && io.desc.includes(ioKey)))
              .sort((a, b) => b.summary_alloc - a.summary_alloc);

            return gl;
          })
          .filter((gl) => gl.children?.length > 0 || !ioKey)
          .sort((a, b) => b.summary_alloc - a.summary_alloc);

        return item;
      })
      .filter((item) => item.children?.length > 0 || (!glKey && !ioKey))
      .sort((a, b) => b.summary_alloc - a.summary_alloc);
  }

  const flattenData = (data: BudgetReportItem[], parentKey: string = "") => {
    let result: any[] = [];
    data.forEach((node) => {
      const flatNode = {
        Description: node.desc,
        Allocated: node.summary_alloc,
        Reserved: node.summary_reserve,
        Used: node.summary_used,
        Paid: node.summary_paid,
        Remaining: node.summary_remaining,
      };

      result.push(flatNode);

      if (node.children && node.children.length > 0) {
        const children = flattenData(node.children, node.key);
        result = result.concat(children);
      }
    });

    return result;
  };

  return (
    <Row
      style={{ padding: "1rem" }}
      justify="space-between"
      align="middle"
      gutter={[10, 10]}
    >
      <Col span={24}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col xs={24} md={20}>
            <Form form={form} layout="vertical" onValuesChange={filterFunc}>
              <Row justify="start" align="middle" gutter={[16, 16]}>
                {/* Budget Year and Date Picker */}
                <Col xs={24} sm={12} md={8}>
                  <Row align="middle" gutter={[10, 0]}>
                    <Col flex="100px">
                      <TextHeaderComponents
                        textHeaderProps="Budget Year"
                        textSubProps="ปีงบประมาณ"
                        isRequir
                      />
                    </Col>
                    <Col flex="auto">
                      <Form.Item name="budgetYear" initialValue={moment()}>
                        <DatePicker picker="year" style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>

                {/* Description and Input */}
                <Col xs={24} sm={12} md={8}>
                  <Row align="middle" gutter={[10, 0]}>
                    <Col flex="100px">
                      <TextHeaderComponents
                        textHeaderProps="Description :"
                        textSubProps="รายละเอียด"
                      />
                    </Col>
                    <Col flex="auto">
                      <Form.Item name="description">
                        <Input placeholder="Search by description" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>

                {/* Cost Center */}
                <Col xs={24} sm={12} md={8}>
                  <Row align="middle" gutter={[10, 0]}>
                    <Col flex="100px">
                      <TextHeaderComponents
                        textHeaderProps="Cost center"
                        textSubProps="-"
                      />
                    </Col>
                    <Col flex="auto">
                      <Form.Item name="costCenter">
                        <Select
                          options={filters.cost}
                          placeholder="Select Cost center"
                          style={{ width: "100%" }}
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>

                {/* GL */}
                <Col xs={24} sm={12} md={8}>
                  <Row align="middle" gutter={[10, 0]}>
                    <Col flex="100px">
                      <TextHeaderComponents
                        textHeaderProps="GL"
                        textSubProps="-"
                      />
                    </Col>
                    <Col flex="auto">
                      <Form.Item name="glAccount">
                        <Select
                          options={filters.gl}
                          placeholder="Select GL account"
                          style={{ width: "100%" }}
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>

                {/* IO */}
                <Col xs={24} sm={12} md={8}>
                  <Row align="middle" gutter={[10, 0]}>
                    <Col flex="100px">
                      <TextHeaderComponents
                        textHeaderProps="IO"
                        textSubProps="-"
                      />
                    </Col>
                    <Col flex="auto">
                      <Form.Item name="internalOrder">
                        <Select
                          options={filters.io}
                          placeholder="Select internal order"
                          style={{ width: "100%" }}
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Form>
          </Col>
          <Col xs={24} md={4}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                gap: 10,
              }}
            >
              <Button
                label="Export to excel"
                icon="pi pi-file-excel"
                data-pr-tooltip="XLS"
                loading={loading}
                style={{
                  height: "38px",
                  width: "100%",
                  background: " #28a745",
                  border: "1px solid #28a745",
                  color: "#ffffff",
                  fontSize: "1em",
                  borderRadius: "6px",
                  margin: " 0px 11px 0px 0px",
                  justifyContent: "center",
                }}
                onClick={async () => {
                  await exportExcel([],"BudgetSummary", flattenData(filteredData));
                }}
              />
              <Button
                label="Export Budget Summaries"
                icon="pi pi-file-excel"
                data-pr-tooltip="XLS"
                loading={loading}
                style={{
                  height: "38px",
                  width: "100%",
                  background: " #28a745",
                  border: "1px solid #28a745",
                  color: "#ffffff",
                  fontSize: "1em",
                  borderRadius: "6px",
                  margin: " 0px 11px 0px 0px",
                  justifyContent: "center",
                }}
                onClick={async () => {
                  setLoading(true);
                  let value = form.getFieldsValue();
                  console.log(value);
                  const res = await GetReportBudgetSummaries(value);
                  console.log(res);
                  if (res.Data) {
                    await exportExcel([],"BudgetSummaries", res.Data);
                  }
                  setLoading(false);

                  // value.budgetYear = value.budgetYear.get("year");
                  // getReport(value);
                }}
              />
            </div>
          </Col>
        </Row>
      </Col>
      <Col span={24}>
        <Row justify="end" gutter={[10, 10]}>
          <Col span={24}>
            <Table
             className="budget-table"
              columns={columns}
              dataSource={data}
              loading={loading}
              rowKey={"key"}
              bordered
              size="small"
              scroll={{ x: "100%" }}
              onChange={(pagination) => {
                let value = form.getFieldsValue();
                value.budgetYear = value.budgetYear.get("year");
                getReport(value, pagination.current);
              }}
              pagination={{ pageSize: 10, total: total }}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
}

export default BudgetSummary;
